import { useState, useRef, useEffect } from 'react';
import { AppPage, AppCard } from '../../components/general';
import { Toast } from 'primereact/toast';
import { DataTable, TableDefinition, DataTableSearchCreateHeader } from '../../components/datatable';
import { ProgressBar } from 'primereact/progressbar';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import AppRegistrationService from '../../services/appRegistrationService';
import { AppRegistration, AppRegistrationCreated, AppRegistrationSecret } from '../../models/app-registration';
import CopyableText from '../../components/general/CopyableText';

export const AppRegistrations = (): JSX.Element => {
  const toast = useRef<Toast>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [appRegistrations, setAppRegistrations] = useState<AppRegistration[]>([]);
  const [search, setSearch] = useState<string>('');

  // Create dialog
  const [createDialogVisible, setCreateDialogVisible] = useState<boolean>(false);
  const [newAppName, setNewAppName] = useState<string>('');

  // Secret dialog (shown after create or regenerate)
  const [secretDialogVisible, setSecretDialogVisible] = useState<boolean>(false);
  const [secretDialogTitle, setSecretDialogTitle] = useState<string>('');
  const [secretData, setSecretData] = useState<{ clientId?: string; clientSecret?: string } | null>(null);

  const appRegistrationService = AppRegistrationService.getInstance();

  useEffect(() => {
    fetchAppRegistrations();
  }, []);

  const fetchAppRegistrations = (): void => {
    setIsLoading(true);
    appRegistrationService
      .getAll()
      .then((result) => {
        setAppRegistrations(result ?? []);
      })
      .catch((error) => {
        showError(error.message);
        setAppRegistrations([]);
      })
      .finally(() => setIsLoading(false));
  };

  const showError = (message: string) => {
    toast.current?.show({ severity: 'error', summary: 'Error', detail: message, life: 5000 });
  };

  const showSuccess = (message: string) => {
    toast.current?.show({ severity: 'success', summary: 'Success', detail: message, life: 3000 });
  };

  const onCreateApp = () => {
    setNewAppName('');
    setCreateDialogVisible(true);
  };

  const handleCreate = () => {
    if (!newAppName.trim()) return;

    setIsSubmitting(true);
    setCreateDialogVisible(false);
    appRegistrationService
      .create({ appName: newAppName.trim() })
      .then((result: AppRegistrationCreated) => {
        showSuccess('App registration created successfully');
        setSecretDialogTitle('Application Created');
        setSecretData({ clientId: result.clientId, clientSecret: result.clientSecret });
        setSecretDialogVisible(true);
        fetchAppRegistrations();
      })
      .catch((error) => showError(error.message))
      .finally(() => setIsSubmitting(false));
  };

  const onRegenerateSecret = (app: AppRegistration) => {
    confirmDialog({
      message: `Are you sure you want to regenerate the secret for "${app.displayName}"? The current secret will be invalidated.`,
      header: 'Regenerate Secret',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-warning',
      accept: () => {
        setIsSubmitting(true);
        appRegistrationService
          .regenerateSecret(app.clientId!)
          .then((result: AppRegistrationSecret) => {
            showSuccess('Secret regenerated successfully');
            setSecretDialogTitle('New Client Secret');
            setSecretData({ clientId: result.clientId, clientSecret: result.clientSecret });
            setSecretDialogVisible(true);
          })
          .catch((error) => showError(error.message))
          .finally(() => setIsSubmitting(false));
      },
    });
  };

  const onDelete = (app: AppRegistration) => {
    confirmDialog({
      message: `Are you sure you want to delete "${app.displayName}"? This action cannot be undone.`,
      header: 'Delete App Registration',
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      accept: () => {
        setIsSubmitting(true);
        appRegistrationService
          .delete(app.clientId!)
          .then(() => {
            showSuccess('App registration deleted successfully');
            fetchAppRegistrations();
          })
          .catch((error) => showError(error.message))
          .finally(() => setIsSubmitting(false));
      },
    });
  };

  const actionBodyTemplate = (app: AppRegistration) => {
    return (
      <div className="flex">
        <Button
          icon="pi pi-refresh"
          className="p-button-rounded p-button-text"
          onClick={() => onRegenerateSecret(app)}
          tooltip="Regenerate Secret"
          tooltipOptions={{ position: 'left' }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-text"
          onClick={() => onDelete(app)}
          tooltip="Delete App Registration"
          tooltipOptions={{ position: 'left' }}
          severity="danger"
        />
      </div>
    );
  };

  const tableDef: TableDefinition<AppRegistration> = {
    columns: [
      {
        field: 'displayName',
        header: 'App Name',
        dataType: 'text',
      },
      {
        field: 'clientId',
        header: 'Client ID',
        dataType: 'text',
      },
      {
        field: 'createdDate',
        header: 'Date Created',
        dataType: 'date',
      },
      {
        field: 'createdByName',
        header: 'Created By',
        dataType: 'text',
      },
      {
        header: 'Actions',
        body: actionBodyTemplate,
        dataType: 'none',
        sortable: false,
        filter: false,
      },
    ],
  };

  const createDialogFooter = (
    <div className="flex justify-content-end gap-2">
      <Button label="Cancel" icon="pi pi-times" onClick={() => setCreateDialogVisible(false)} className="p-button-text" />
      <Button label="Create" icon="pi pi-check" onClick={handleCreate} disabled={!newAppName.trim()} loading={isSubmitting} />
    </div>
  );

  const secretDialogFooter = (
    <div className="flex justify-content-end">
      <Button label="Close" onClick={() => setSecretDialogVisible(false)} />
    </div>
  );

  return (
    <AppPage title="App Registrations" breadcrumbs="Admin - App Registrations">
      <AppCard>
        <Toast ref={toast} />
        <ConfirmDialog />

        {/* Create App Dialog */}
        <Dialog
          header="Create App Registration"
          visible={createDialogVisible}
          onHide={() => setCreateDialogVisible(false)}
          style={{ width: '450px' }}
          footer={createDialogFooter}
        >
          <div className="flex flex-column gap-3 pt-3">
            <label htmlFor="appName" className="font-semibold">
              Application Name
            </label>
            <InputText
              id="appName"
              value={newAppName}
              onChange={(e) => setNewAppName(e.target.value)}
              placeholder="Enter application name"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newAppName.trim()) handleCreate();
              }}
              autoFocus
            />
          </div>
        </Dialog>

        {/* Secret Display Dialog */}
        <Dialog
          header={secretDialogTitle}
          visible={secretDialogVisible}
          onHide={() => setSecretDialogVisible(false)}
          style={{ width: '550px' }}
          footer={secretDialogFooter}
          closable={false}
        >
          <div className="flex flex-column gap-4 pt-3">
            <div className="p-message p-message-warn p-component flex align-items-center gap-2 p-3 border-round">
              <i className="pi pi-exclamation-triangle text-yellow-500" style={{ fontSize: '1.25rem' }} />
              <span className="text-sm">
                Make sure to copy the client secret now. You will not be able to see it again.
              </span>
            </div>
            <div className="flex flex-column gap-2">
              <label className="text-sm text-gray-500">Client ID</label>
              <CopyableText value={secretData?.clientId ?? null} label="Client ID" toast={toast} />
            </div>
            <div className="flex flex-column gap-2">
              <label className="text-sm text-gray-500">Client Secret</label>
              <CopyableText value={secretData?.clientSecret ?? null} label="Client Secret" toast={toast} />
            </div>
          </div>
        </Dialog>

        {isSubmitting && (
          <div className="w-full mb-3">
            <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
          </div>
        )}

        <DataTable
          loading={isLoading}
          header={<DataTableSearchCreateHeader onCreate={onCreateApp} onSearchChange={setSearch} />}
          tableDef={tableDef}
          data={appRegistrations}
          globalFilterValue={search}
        />
      </AppCard>
    </AppPage>
  );
};
